/**
 * Field Help — Tickets
 *
 * Keys MUST match the ticket field ids in
 * `features/tickets/config/fields/*.ts` (see `ticketFields` / `TicketFieldId`)
 * so the "?" popover attaches to the right column / editor row / card field.
 *
 * Relationship context (used in `relatedTo`): a ticket is assigned to exactly
 * ONE contact (direct edge — the person reporting/owning the MSP support work);
 * the company auto-resolves from that contact (ticket → contact → company).
 * Time entries attach to a ticket and roll up cost/hours
 * (time_entry → ticket → contact → company). See
 * `lib/actions/relationships/AGENTS.md`.
 *
 * Tickets are WTC's unit of MSP support work — the day-to-day backlog
 * engineers triage, work, and bill against.
 */

import type { EntityFieldHelp } from "./types";

export const ticketFieldHelp: EntityFieldHelp = {
  template: {
    title: "Template",
    description:
      "Pre-built ticket template that pre-fills title, description, tags, priority, and status.",
    usage:
      "Pick a template to spin up a common request (onboarding, password reset, hardware swap) without retyping. It's a starting point — every field stays editable after applying.",
  },
  ticket_number: {
    title: "Ticket #",
    description: "Auto-generated sequential number identifying the ticket.",
    usage:
      "Read-only. Use it when referencing the ticket in email, Slack, or with a client (\"re: #1042\").",
  },
  title: {
    title: "Title",
    description: "Short summary of the issue — what's wrong and for whom.",
    usage:
      "Keep it scannable in the queue (e.g. \"Acme — Outlook won't sync\"). It's the headline an engineer sees when triaging the backlog.",
  },
  description: {
    title: "Description",
    description: "Full detail of the issue — symptoms, scope, and context.",
    usage:
      "Capture enough that an engineer can start working without re-interviewing the client. Searchable, so include error text and affected systems.",
  },
  contact_id: {
    title: "Contact",
    description:
      "The person this ticket is for — the requester or affected user. Every ticket is assigned to exactly one contact.",
    usage:
      "Required. Set this first — it's how the ticket connects to a person and, through them, a company.",
    relatedTo:
      "Direct edge: ticket → contact. The Company is resolved automatically from this contact, so you don't link the company separately.",
  },
  company_id: {
    title: "Company",
    description:
      "The client company the ticket belongs to. Auto-populated from the selected contact.",
    usage:
      "Read-only on the form — change the Contact to change the company. Drives which client the work and any billing roll up to.",
    relatedTo: "Resolved via ticket → contact → company. Not linked directly.",
  },
  company_name: {
    title: "Company Name",
    description: "Display name of the ticket's company (denormalized for fast filtering).",
    usage: "Read-only label for scanning the queue by client. Set by the contact's company link.",
    relatedTo: "Mirrors the resolved Company record.",
  },
  contact_name: {
    title: "Contact Name",
    description: "Display name of the assigned contact (denormalized for fast filtering).",
    usage: "Read-only label for scanning the queue by requester. Set by the linked Contact.",
    relatedTo: "Mirrors the linked Contact record.",
  },
  status: {
    title: "Status",
    description:
      "Where the ticket sits in the support lifecycle — new, assigned, in_progress, waiting_for_customer, waiting_for_vendor, resolved, closed, or cancelled.",
    usage:
      "Move it as work progresses. \"waiting_for_customer\"/\"waiting_for_vendor\" pause your clock without losing the ticket; resolved/closed pull it out of the open queue. Accurate status is what makes the backlog trustworthy.",
  },
  priority: {
    title: "Priority",
    description: "Urgency of the ticket — low, medium, high, or urgent.",
    usage:
      "Sets the order engineers work the queue. Reserve urgent for client-down / business-impacting issues so it stays meaningful.",
  },
  category: {
    title: "Category",
    description:
      "Type of support work — backup, cloud, email, hardware, network, phone, printer, security, software, or other.",
    usage:
      "Drives reporting on where support time goes and helps route to the right engineer. Tag it accurately to surface trends (e.g. recurring network issues at one client).",
  },
  assigned_to: {
    title: "Assigned To",
    description: "The engineer responsible for working the ticket.",
    usage:
      "Set this so the ticket appears in someone's \"my tickets\" queue. Unassigned tickets are the ones that fall through the cracks — surface and claim them promptly.",
    relatedTo: "Drives utilization and who's accountable for resolution.",
  },
  created_by: {
    title: "Created By",
    description: "The user who opened the ticket.",
    usage: "Read-only. Useful for tracing who logged a request and following up on context.",
  },
  due_date: {
    title: "Due Date",
    description: "When the ticket should be resolved by.",
    usage:
      "Drives the overdue filter — any ticket past due and not completed surfaces as overdue. Set realistic dates so the overdue queue means something.",
    relatedTo: "Compared against SLA Hours when judging whether response targets are being met.",
  },
  estimated_hours: {
    title: "Estimated Hours",
    description: "Expected effort to resolve the ticket, in hours.",
    usage:
      "Set up front to plan capacity and set client expectations. Compare against Actual Hours to spot tickets that ran long.",
    relatedTo: "Benchmarked against Actual Hours logged via time entries.",
  },
  actual_hours: {
    title: "Actual Hours",
    description: "Total hours actually worked, rolled up from time entries on the ticket.",
    usage:
      "Read-only — accumulates as engineers log time. The real cost of the work; compare to Estimated Hours to refine future estimates.",
    relatedTo: "Summed from time entries (time_entry → ticket). Feeds Total Cost.",
  },
  billable_hours: {
    title: "Billable Hours",
    description: "Portion of worked hours that can be billed to the client, in hours.",
    usage:
      "Read-only — derived from billable time entries. The basis for what the client owes; non-billable work is excluded here.",
    relatedTo: "A subset of Actual Hours; multiplied by Hourly Rate toward Total Billed.",
  },
  hourly_rate: {
    title: "Hourly Rate",
    description: "Billing rate applied to this ticket's work, in dollars per hour.",
    usage:
      "Read-only on the ticket — sourced from the contract/company rate. Drives how billable hours convert to dollars.",
    relatedTo: "Multiplied by Billable Hours to produce Total Billed.",
  },
  total_cost: {
    title: "Total Cost",
    description:
      "Total cost of this ticket TO THE CLIENT — what the client is charged for the work (billable hours × billable rate). Auto-calculated from logged time entries.",
    usage:
      "Read-only, admin-only. This is the CLIENT-facing cost, NOT WTC's internal labor cost. Do not use it for owner profitability/cost math — internal labor cost = employee pay rate × 1.20 burden (see lib/finance/labor-cost.ts).",
    relatedTo: "Derived from Billable Hours × Hourly Rate (the client's billing rate).",
  },
  total_billed: {
    title: "Total Billed",
    description: "Total dollar amount billed to the client for this ticket.",
    usage:
      "Admin-only. The revenue side of the ticket; Total Billed minus Total Cost is the margin on the support work.",
    relatedTo: "Driven by Billable Hours × Hourly Rate.",
  },
  sla_hours: {
    title: "SLA Hours",
    description: "Service-level-agreement target window for this ticket, in hours.",
    usage:
      "Set per the client's contract. Measure Due Date / resolution time against it to confirm WTC is meeting committed response and resolution targets.",
    relatedTo: "Compared against Due Date and resolution timestamps.",
  },
  escalation_level: {
    title: "Escalation Level",
    description: "How far the ticket has been escalated (0 = none, higher = more escalated).",
    usage:
      "Bump it when a ticket needs senior attention or is breaching SLA. A rising escalation level is a signal to step in before the client relationship suffers.",
    relatedTo: "Often paired with Auto Escalate.",
  },
  tags: {
    title: "Tags",
    description: "Free-form labels for segmenting tickets (e.g. recurring issue, project, root-cause).",
    usage:
      "Use for your own grouping and trend-spotting beyond Category — e.g. tagging every ticket tied to one failing server.",
  },
  internal_notes: {
    title: "Internal Notes",
    description: "Notes visible only to WTC staff — never shown to the customer.",
    usage:
      "Diagnostic detail, vendor case numbers, internal caveats, anything you wouldn't put in front of the client. Keep customer-facing wording in Customer Notes.",
  },
  customer_visible_notes: {
    title: "Customer Notes",
    description: "Notes intended to be shared with the customer.",
    usage:
      "Status updates and resolution summaries the client should see. Write these as if the client is reading them — because they can.",
  },
  is_internal: {
    title: "Internal Ticket",
    description: "Marks the ticket as internal WTC work rather than a client request.",
    usage:
      "Turn on for tickets that don't belong to a paying client (internal IT, process work) so they're separated from billable client support.",
  },
  has_time_entries: {
    title: "Has Time Entries",
    description: "Whether any time has been logged against the ticket.",
    usage:
      "Read-only, auto-maintained. Filter on it to find worked-but-unbilled tickets, or tickets sitting open with no effort yet logged.",
    relatedTo: "Set automatically when time entries are added (time_entry → ticket).",
  },
  auto_assign: {
    title: "Auto Assign",
    description: "Whether the ticket is eligible for automatic assignment to an engineer.",
    usage: "Enable to let routing pick an assignee instead of assigning by hand.",
  },
  auto_escalate: {
    title: "Auto Escalate",
    description: "Whether the ticket escalates automatically when it ages or breaches SLA.",
    usage:
      "Enable so neglected or SLA-breaching tickets bump their Escalation Level without manual tracking.",
    relatedTo: "Drives Escalation Level when triggered.",
  },
  is_software: {
    title: "Software",
    description: "Denormalized flag indicating the ticket relates to software work.",
    usage: "Read-only. Used for reporting and filtering software vs. infrastructure support.",
  },
  created_at: {
    title: "Created",
    description: "When the ticket was opened.",
    usage: "Read-only. Useful for queue aging — how long a request has been outstanding.",
  },
  updated_at: {
    title: "Last Updated",
    description: "When the ticket was last modified.",
    usage: "Read-only. A staleness signal — a long-untouched open ticket is a prompt to act.",
  },
  resolved_at: {
    title: "Resolved",
    description: "When the ticket was marked resolved.",
    usage: "Read-only. Anchors resolution-time reporting and SLA measurement.",
    relatedTo: "Measured against Created and SLA Hours.",
  },
  closed_at: {
    title: "Closed",
    description: "When the ticket was closed.",
    usage: "Read-only. Final close-out timestamp, after any post-resolution confirmation.",
  },
};
